import { useEffect, useState } from "react";
import { Link } from "wouter";
import { CalendarDays, Inbox, Users, ClipboardList, Loader2 } from "lucide-react";
import { CoordinatorLayout } from "@/components/layout/coordinator-layout";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface DashboardData {
  pendingAppointments: number;
  openQueries: number;
  patientCount: number;
  teamCount: number;
}

export default function CoordinatorDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${BASE}/api/coordinator/dashboard`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => setData(d as DashboardData))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <CoordinatorLayout>
      <div className="p-8 max-w-6xl">
        <div className="flex items-center gap-3 mb-2">
          <ClipboardList className="h-6 w-6 text-sky-600" />
          <h1 className="text-2xl font-bold text-slate-900">Coordinator dashboard</h1>
        </div>
        <p className="text-slate-600 mb-8">
          Practice operations at a glance — appointments, advice queue and team roster.
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            Couldn't load dashboard: {error}
          </p>
        )}

        {data && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              href="/coordinator/appointments"
              icon={CalendarDays}
              label="Pending appointments"
              value={data.pendingAppointments}
              tone="sky"
              testid="stat-appointments"
            />
            <StatCard
              href="/coordinator/queries"
              icon={Inbox}
              label="Open advice queries"
              value={data.openQueries}
              tone="amber"
              testid="stat-queries"
            />
            <StatCard
              href="/coordinator/patients"
              icon={Users}
              label="Patients seen"
              value={data.patientCount}
              tone="slate"
              testid="stat-patients"
            />
            <StatCard
              href="/coordinator/team"
              icon={Users}
              label="Team members"
              value={data.teamCount}
              tone="emerald"
              testid="stat-team"
            />
          </div>
        )}
      </div>
    </CoordinatorLayout>
  );
}

function StatCard({
  href,
  icon: Icon,
  label,
  value,
  tone,
  testid,
}: {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  tone: "sky" | "amber" | "slate" | "emerald";
  testid: string;
}) {
  const tones = {
    sky: "bg-sky-50 text-sky-700",
    amber: "bg-amber-50 text-amber-700",
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
  } as const;
  return (
    <Link
      href={href}
      className="block bg-white p-5 rounded-2xl border border-slate-200 hover:border-sky-300 hover:shadow-sm transition-all"
      data-testid={testid}
    >
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-3 ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-3xl font-bold text-slate-900">{value}</p>
      <p className="text-sm text-slate-600 mt-0.5">{label}</p>
    </Link>
  );
}
